import moment from "moment";
import { BookingType } from "../../_enums/bookingTypes.enum";
import { CandidatePay } from "../../_enums/candidatePay.enum";
import { PayType } from "../../_enums/payType.enum";
import { BookingDayInterface } from "../../_types/bookings";
import { ProfitMargin, round } from "../helpers";

/**
 * Calculate effective break minutes for a booking day.
 * Prioritizes day-specific breaks over global breaks.
 * @param day - The booking day object
 * @param breakMinutes - The global break minutes to use as fallback
 * @returns The effective break minutes to apply
 */
export const getEffectiveBreakMinutes = (day: BookingDayInterface, breakMinutes: number): number => {
	const dayBreakMinutes = day.breaks ? parseFloat(day.breaks) * 60 : 0;
	const effectiveBreakMinutes = day.breaks ? dayBreakMinutes : breakMinutes; // Use day-specific breaks when provided, otherwise fall back to global breaks
	return effectiveBreakMinutes;
};

export const BookingUnits = (PAY_TYPE: PayType, DAYS: BookingDayInterface[] = [], BREAKS?: string) => {
	if (PAY_TYPE === PayType.Day) return DAYS.length;

	let units = 0;
	let breakMinutes = 0;
	if (BREAKS) {
		breakMinutes = parseFloat(BREAKS) * 60;
	}
	for (let i = 0, day: BookingDayInterface; (day = DAYS[i++]); ) {
		let startMoment = moment(day.start);
		let endMoment = moment(day.end);

		// Check if end time is before start time (overnight shift)
		if (endMoment.isBefore(startMoment)) {
			endMoment = endMoment.add(1, "day");
		}
		let minutes = endMoment.diff(startMoment, "minutes");

		const effectiveBreakMinutes = getEffectiveBreakMinutes(day, breakMinutes);
		units += (minutes - effectiveBreakMinutes) / 60;
	}
	return round(units, 2);
};

export const UnitAmount = (DAY: BookingDayInterface, PAY_TYPE: PayType, CHARGE_RATE: string, BREAKS?: string) => {
	let unitAmount = 0;

	const dcr = DAY.chargeRate ? parseFloat(DAY.chargeRate) : parseFloat(CHARGE_RATE); // day pay rate

	let minutes = moment(DAY.end).diff(moment(DAY.start), "minutes"); // if the pay type is hourly the minutes will be used to calculate the pay rate

	let breakMinutes = BREAKS ? parseFloat(BREAKS) * 60 : 0;
	const effectiveBreakMinutes = getEffectiveBreakMinutes(DAY, breakMinutes);
	const minutesAfterBreaks = minutes - effectiveBreakMinutes;

	!PAY_TYPE || PAY_TYPE == PayType.Day ? (unitAmount = unitAmount + dcr) : (unitAmount = unitAmount + (dcr * minutesAfterBreaks) / 60);

	return unitAmount;
};

export const calculateAgencyFeeFromMargin = ({
	chargeRate,
	payRate,
	days,
	payType,
	oncosts,
	candidatePayType,
	bookingType,
	breaks,
}: {
	chargeRate?: string;
	payRate?: string;
	days?: BookingDayInterface[];
	payType?: PayType;
	oncosts?: number;
	candidatePayType?: CandidatePay;
	bookingType?: BookingType;
	breaks?: string;
	umbrellaAssignmentFee?: number;
}): string | null => {
	const charge = parseFloat(chargeRate || "");
	const pay = parseFloat(payRate || "");

	if (isNaN(charge) || isNaN(pay) || !days?.length) {
		return null;
	}

	const margin = ProfitMargin(
		`${pay}`,
		`${charge}`,
		days,
		payType,
		oncosts || 0,
		candidatePayType || CandidatePay.Paye,
		bookingType,
		false,
		undefined,
		undefined,
		breaks,
		0
	);

	if (isNaN(margin)) {
		return null;
	}

	return round(margin / days.length, 2).toFixed(2);
};
